import { createSampleAudio } from './187-sample-audio.mjs';
const names = ['bounce', 'pickup', 'finish', 'checkpoint', 'stumble', 'crate', 'spring', 'shield'];
/** Bob owns cue selection and per-frame arbitration; sample loading, gesture
 * unlock, voice bounds and lifecycle are shared with the board games. */
export function createBobAudio(options = {}) {
  const audio = createArcadeAudio(options);
  function play(event) {
    if (event.type === 'food' && event.reaction) return;
    if (event.type === 'arcade' && !event.tierChanged) return;
    if (event.type === 'chain-reaction' && !(event.count > 0)) return;
    const name = {
      launch: 'bounce',
      food: 'pickup',
      complete: 'finish',
      'bonus-route': 'finish',
      checkpoint: 'checkpoint',
      stumble: 'stumble',
      fail: 'stumble',
      crate: 'crate',
      spring: 'spring',
      shield: 'shield',
      powerup: 'shield',
      arcade: 'checkpoint',
      'chain-reaction': 'pickup',
    }[event.type];
    if (!name) return;
    const chain = event.type === 'arcade' || event.type === 'chain-reaction';
    const rate =
      event.type === 'arcade'
        ? 2 ** (((Math.max(2, Math.min(4, event.multiplier || 2)) - 2) * 4) / 12)
        : event.type === 'chain-reaction'
          ? 1.18 + Math.min(6, event.count) * 0.035
          : name === 'pickup'
            ? 1 + Math.min(4, event.combo || 0) * 0.06
            : 1;
    audio.play(name, {
      rate,
      level: chain ? 0.65 : 1,
      channel: chain ? 'arcade' : name,
      interval: chain ? 0.22 : name === 'pickup' ? 0.08 : 0.04,
    });
  }
  // One simulation frame may contain a crate, six collected goodies and several
  // tier advances. Resolve it before playback: one highest-tier motif replaces
  // the cascade pickup sound; damage takes precedence over all celebration.
  function playEvents(events) {
    const damage = events.findLast((event) => ['stumble', 'fail'].includes(event.type));
    if (damage) {
      audio.stop();
      play(damage);
      return;
    }
    const tiers = events.filter((event) => event.type === 'arcade' && event.tierChanged);
    const highlight =
      tiers.reduce(
        (best, event) => (!best || event.multiplier >= best.multiplier ? event : best),
        null,
      ) || events.findLast((event) => event.type === 'chain-reaction');
    if (highlight) play(highlight);
    for (const event of events) if (!['arcade', 'chain-reaction'].includes(event.type)) play(event);
  }
  return {
    ...audio,
    play,
    playEvents,
    async test() {
      if (!(await audio.enable(true))) return false;
      audio.play('bounce', { channel: 'test' });
      return true;
    },
  };
}

/** Board and custom arcade cues share the gesture-bound sampler and its recovery
 * epochs. The historical cue options map to the canonical sample API; no second
 * context, fetch queue or voice collection is created by this adapter. */
export function createArcadeAudio(options = {}) {
  const audio = createSampleAudio({
    ...options,
    samples: Object.fromEntries(
      names.map((name) => [name, new URL(`../assets/bob-audio-${name}.wav`, import.meta.url)]),
    ),
  });
  return {
    ...audio,
    cue(name, { rate = 1, volume = 1, channel = name, cooldown = 0.06 } = {}) {
      return audio.play(name, {
        rate: Number.isFinite(rate) ? Math.max(0.5, Math.min(2, rate)) : 1,
        level: Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 1,
        channel,
        interval: cooldown,
      });
    },
  };
}
